/**
 * 설정 > 계정 관리 UI 타입·헬퍼·목 데이터.
 * API 연동 시 `mapAdminUser*ToRow`로 AdminUser* 응답을 목록/상세 행으로 변환한다.
 */
import type {
  AdminMenuMetadataItem,
  AdminRoleName,
  AdminUserDetail,
  AdminUserDisplayStatus,
  AdminUserSummary,
  MenuPermissionSaveItem,
} from '@/entities/admin-user/types';
import type { BadgeProps } from '@/shared/components/ui/badge';
import {
  TOP_LEVEL_MENU_LABELS,
  TOP_LEVEL_MENU_ORDER,
  type TopLevelMenuCode,
} from '@/shared/utils/authPermissions';
import { normalizeAuthRole, type AuthRoleCode } from '@/shared/utils/authRoles';
import {
  DISPLAY_EMPTY,
  formatDate,
  formatDateTime,
} from '@/shared/lib/dateFormat';

/** 화면 표시용 계정 상태 — API `displayStatus`와 동일 */
export type AdminAccountStatus = AdminUserDisplayStatus;

/** 메뉴 권한 수준 — 없음 / 조회 / 편집 */
export type MenuAccessLevel = 'NONE' | 'READ' | 'WRITE';

export type AdminAccountRow = {
  id: string;
  username?: string;
  email: string;
  name: string;
  role: AuthRoleCode | string;
  department: string;
  status: AdminAccountStatus;
  lastLoginAt: string | null;
  createdAt: string;
  phone: string;
  createdBy: string;
  lastLoginIp: string | null;
  lockedAt?: string | null;
  failedAuthenticationCount?: number | null;
  mustChangePassword?: boolean;
  temporaryPasswordExpiresAt?: string | null;
  rowVersion?: number;
  deletedAt?: string | null;
};

export type MenuPermissionRow = {
  id: string;
  label: string;
  /** 상위 메뉴면 false — 들여쓰기 표시 */
  child?: boolean;
  /** 메타데이터 parentMenuCode — 상속·롤업용 */
  parentId?: string | null;
  level: MenuAccessLevel;
  /** true면 상위 상속 — 저장 시 미변경이면 permissionLevel null */
  inherited?: boolean;
};

export type LoginActivityRow = {
  id: string;
  at: string;
  ip: string;
  device: string;
  result: 'SUCCESS' | 'FAILURE';
  note: string;
};

export type AccountChangeRow = {
  id: string;
  at: string;
  summary: string;
  performedBy: string;
};

const ACCOUNT_STATUS_LABELS: Record<AdminAccountStatus, string> = {
  ACTIVE: '활성',
  TEMPORARY_PASSWORD: '임시비밀번호',
  LOCKED: '잠금',
  INACTIVE: '비활성',
  DELETED: '삭제',
};

export const getAccountStatusLabel = (status: AdminAccountStatus): string =>
  ACCOUNT_STATUS_LABELS[status] ?? status;

export const getAccountStatusBadgeVariant = (
  status: AdminAccountStatus,
): NonNullable<BadgeProps['variant']> => {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'TEMPORARY_PASSWORD':
      return 'warning';
    case 'LOCKED':
      return 'destructive';
    case 'INACTIVE':
      return 'secondary';
    case 'DELETED':
      return 'outline';
    default:
      return 'outline';
  }
};

/** ISO → RDS 표시 포맷 (`YYYY.MM.DD` / `YYYY.MM.DD HH:mm:ss`) */
export const formatAdminDateTime = (
  value: string | null | undefined,
  withTime = true,
  _withSeconds = true,
): string | null => {
  if (!value) return null;
  if (!withTime) {
    const formatted = formatDate(value);
    return formatted === DISPLAY_EMPTY ? value : formatted;
  }
  const formatted = formatDateTime(value);
  if (formatted === DISPLAY_EMPTY) return value;
  return formatted;
};

/**
 * UI 역할 코드 → API `roleName`.
 * 생성 시 일반은 `GENERAL`, 관리(ROLE_MANAGER)는 `ROLE_MANAGER`.
 * 레거시 ROLE_ADMIN은 관리로 보낸다.
 */
export const toAdminApiRoleName = (role: string): AdminRoleName => {
  const normalized = normalizeAuthRole(role);
  switch (normalized) {
    case 'ROLE_GENERAL':
    case 'GENERAL':
      return 'GENERAL';
    case 'ROLE_MANAGER':
      return 'ROLE_MANAGER';
    case 'ROLE_MASTER':
      return 'ROLE_MASTER';
    default:
      return 'GENERAL';
  }
};

/**
 * full-access 역할 (사이드바·가드).
 * ROLE_MANAGER는 생성 시 메뉴 권한을 둘 수 있으므로, 권한 저장 면제는
 * `isMenuPermissionExemptRole`을 사용한다.
 */
export const isFullAccessAdminRole = (
  role: string | null | undefined,
): boolean => {
  const normalized = normalizeAuthRole(role);
  return normalized === 'ROLE_MASTER' || normalized === 'ROLE_MANAGER';
};

/**
 * 메뉴 권한 row를 두지 않는 역할 (전체 권한 계정).
 * MASTER / MANAGER는 모든 메뉴 WRITE — 편집 UI는 읽기 전용.
 */
export const isMenuPermissionExemptRole = (
  role: string | null | undefined,
): boolean => {
  return isFullAccessAdminRole(role);
};

/** 목록 필터 roleName — UI ROLE_GENERAL → API GENERAL, 레거시 ROLE_ADMIN → ROLE_MANAGER */
export const toAdminListRoleFilter = (
  roleFilter: string,
): string | undefined => {
  if (!roleFilter || roleFilter === 'all') return undefined;
  const normalized = normalizeAuthRole(roleFilter);
  if (normalized === 'ROLE_GENERAL' || roleFilter === 'GENERAL') {
    return 'GENERAL';
  }
  return normalized || roleFilter;
};

/** 목록 필터 status — 구 TEMP_PASSWORD 값 호환 */
export const toAdminListStatusFilter = (
  statusFilter: string,
): AdminUserDisplayStatus | undefined => {
  if (!statusFilter || statusFilter === 'all') return undefined;
  if (statusFilter === 'TEMP_PASSWORD') return 'TEMPORARY_PASSWORD';
  return statusFilter as AdminUserDisplayStatus;
};

/**
 * UI 권한 행 → API 저장 항목.
 * 「없음」(NONE)은 permissionLevel null — 상위 상속·override 미생성.
 */
export const toMenuPermissionSaveItems = (
  rows: MenuPermissionRow[],
): MenuPermissionSaveItem[] =>
  rows.map((row) => {
    // 상속 유지 — effectivePermission을 보내면 override가 생긴다
    if (row.inherited) {
      return { menuCode: row.id, permissionLevel: null };
    }
    return {
      menuCode: row.id,
      // NONE은 override 삭제로 취급 (상속/미설정). 명시 NONE이 필요하면 서버 정책에 맞춘다.
      permissionLevel: row.level === 'NONE' ? null : row.level,
    };
  });

/**
 * 문서 기준 메뉴 권한 카탈로그 (표시명·정렬 폴백).
 * id = menuCode. 실제 노출 목록은 GET menu-permissions/metadata 응답을 우선한다.
 */
export const ADMIN_MENU_CATALOG: MenuPermissionRow[] = [
  { id: 'BASIC_INFO', label: '기본 정보', level: 'NONE' },
  { id: 'HOSPITAL', label: '병의원', child: true, level: 'NONE' },
  { id: 'PHARMACY', label: '문전약국', child: true, level: 'NONE' },
  { id: 'PHARMA_COMPANY', label: '제약사', child: true, level: 'NONE' },
  { id: 'DRUG', label: '의약품', child: true, level: 'NONE' },
  {
    id: 'SETTLEMENT_COMMISSION',
    label: '정산 수수료',
    child: true,
    level: 'NONE',
  },
  { id: 'USER', label: '사용자', level: 'NONE' },
  { id: 'USER_MANAGEMENT', label: '사용자관리', child: true, level: 'NONE' },
  {
    id: 'CONTRACT_APPENDIX',
    label: '계약 별첨 관리',
    child: true,
    level: 'NONE',
  },
  { id: 'BASE_FEE_RATE', label: '기본 수수료율', child: true, level: 'NONE' },
  {
    id: 'DETAIL_FEE_RATE',
    label: '상세 수수료율',
    child: true,
    level: 'NONE',
  },
  { id: 'PARTNER', label: '거래처', level: 'NONE' },
  { id: 'PRESCRIPTION', label: '처방', level: 'NONE' },
  {
    id: 'PRESCRIPTION_REVIEW',
    label: '처방 검수',
    child: true,
    level: 'NONE',
  },
  {
    id: 'FINALIZED_PRESCRIPTION',
    label: '확정 처방',
    child: true,
    level: 'NONE',
  },
  { id: 'SETTLEMENT', label: '정산', level: 'NONE' },
  {
    id: 'PHARMA_SETTLEMENT',
    label: '제약사 정산 정보',
    child: true,
    level: 'NONE',
  },
  {
    id: 'SETTLEMENT_MANAGEMENT',
    label: '정산 내역 관리',
    child: true,
    level: 'NONE',
  },
  {
    id: 'REVERSE_TAX_INVOICE',
    label: '세금계산서 역발행',
    child: true,
    level: 'NONE',
  },
  { id: 'MAX_MATCH', label: '맥스매치', level: 'NONE' },
  {
    id: 'MAX_MATCH_STANDARD',
    label: '맥스매치',
    child: true,
    level: 'NONE',
  },
  {
    id: 'MAX_MATCH_FULL',
    label: '맥스매치 Full',
    child: true,
    level: 'NONE',
  },
  {
    id: 'MAX_MATCH_LITE',
    label: '맥스매치 Lite',
    child: true,
    level: 'NONE',
  },
  {
    id: 'MAX_MATCH_OPERATION',
    label: '맥스매치 운영',
    child: true,
    level: 'NONE',
  },
  {
    id: 'MAX_MATCH_OPERATING_HOURS',
    label: '운영 시간',
    child: true,
    level: 'NONE',
  },
  { id: 'NOTICE_FAQ', label: '공지/FAQ', level: 'NONE' },
  { id: 'NOTICE', label: '공지사항', child: true, level: 'NONE' },
  { id: 'FAQ', label: 'FAQ', child: true, level: 'NONE' },
  { id: 'TERMS', label: '약관 관리', child: true, level: 'NONE' },
  { id: 'SETTINGS', label: '설정', child: false, level: 'NONE' },
  { id: 'MENU_MANAGEMENT', label: '메뉴 관리', child: true, level: 'NONE' },
];

/** menuCode → 한국어 표시명 (카탈로그 + top-level 라벨) */
const ADMIN_MENU_LABEL_BY_CODE: Record<string, string> = {
  ...TOP_LEVEL_MENU_LABELS,
  ...Object.fromEntries(ADMIN_MENU_CATALOG.map((row) => [row.id, row.label])),
};

const catalogOrderIndex = (menuCode: string): number => {
  const index = ADMIN_MENU_CATALOG.findIndex((row) => row.id === menuCode);
  return index >= 0 ? index : Number.MAX_SAFE_INTEGER;
};

const topLevelOrderIndex = (menuCode: string): number => {
  const index = TOP_LEVEL_MENU_ORDER.indexOf(menuCode as TopLevelMenuCode);
  return index >= 0 ? index : Number.MAX_SAFE_INTEGER;
};

const resolveMenuLabel = (menuCode: string): string =>
  ADMIN_MENU_LABEL_BY_CODE[menuCode] ?? menuCode;

/**
 * GET /admin/admin-users/menu-permissions/metadata → 권한 테이블 행.
 * 서버 메뉴 코드·부모 관계를 기준으로 상위→하위 순서로 펼친다.
 */
export const mapMenuMetadataToPermissionRows = (
  menus: AdminMenuMetadataItem[],
  defaultLevel: MenuAccessLevel = 'NONE',
): MenuPermissionRow[] => {
  if (!menus.length) return [];

  const childrenByParent = new Map<string, AdminMenuMetadataItem[]>();
  const roots: AdminMenuMetadataItem[] = [];

  for (const item of menus) {
    const isRoot = item.topLevel || !item.parentMenuCode;
    if (isRoot) {
      roots.push(item);
      continue;
    }
    const parent = item.parentMenuCode as string;
    const siblings = childrenByParent.get(parent) ?? [];
    siblings.push(item);
    childrenByParent.set(parent, siblings);
  }

  const sortRoots = (items: AdminMenuMetadataItem[]) =>
    [...items].sort((a, b) => {
      const byTop =
        topLevelOrderIndex(a.menuCode) - topLevelOrderIndex(b.menuCode);
      if (byTop !== 0) return byTop;
      return catalogOrderIndex(a.menuCode) - catalogOrderIndex(b.menuCode);
    });

  const sortChildren = (items: AdminMenuMetadataItem[]) =>
    [...items].sort(
      (a, b) => catalogOrderIndex(a.menuCode) - catalogOrderIndex(b.menuCode),
    );

  const rows: MenuPermissionRow[] = [];
  const visited = new Set<string>();

  const appendItem = (item: AdminMenuMetadataItem, child: boolean) => {
    if (visited.has(item.menuCode)) return;
    visited.add(item.menuCode);
    const parentId = child || item.parentMenuCode ? item.parentMenuCode : null;
    rows.push({
      id: item.menuCode,
      label: resolveMenuLabel(item.menuCode),
      ...(child ? { child: true } : {}),
      parentId: parentId ?? null,
      level: defaultLevel,
    });
    const children = sortChildren(childrenByParent.get(item.menuCode) ?? []);
    for (const childItem of children) {
      appendItem(childItem, true);
    }
  };

  for (const root of sortRoots(roots)) {
    appendItem(root, false);
  }

  // 부모가 메타데이터에 없는 고아 메뉴도 누락하지 않음
  for (const item of menus) {
    if (!visited.has(item.menuCode)) {
      appendItem(item, Boolean(item.parentMenuCode && !item.topLevel));
    }
  }

  return rows;
};

const MENU_ACCESS_RANK: Record<MenuAccessLevel, number> = {
  NONE: 0,
  READ: 1,
  WRITE: 2,
};

/** 하위 권한 중 가장 높은 수준 (조회+편집 혼합 → 편집) */
export const maxMenuAccessLevel = (
  levels: MenuAccessLevel[],
): MenuAccessLevel => {
  let max: MenuAccessLevel = 'NONE';
  for (const level of levels) {
    if (MENU_ACCESS_RANK[level] > MENU_ACCESS_RANK[max]) {
      max = level;
    }
  }
  return max;
};

/**
 * 메뉴 권한 변경.
 * - 상위 변경 시 모든 하위가 동일 수준을 따름
 * - 하위 변경 시 상위는 자식들의 max로 롤업 (조회+편집 → 편집 유지)
 */
export const applyMenuPermissionChange = (
  rows: MenuPermissionRow[],
  id: string,
  level: MenuAccessLevel,
): MenuPermissionRow[] => {
  if (!rows.some((row) => row.id === id)) return rows;

  const childrenByParent = new Map<string, string[]>();
  for (const row of rows) {
    if (!row.parentId) continue;
    const siblings = childrenByParent.get(row.parentId) ?? [];
    siblings.push(row.id);
    childrenByParent.set(row.parentId, siblings);
  }

  const nextLevels = new Map(rows.map((row) => [row.id, row.level]));
  nextLevels.set(id, level);

  // 상위 → 하위 일괄 반영
  const stack = [...(childrenByParent.get(id) ?? [])];
  while (stack.length > 0) {
    const childId = stack.pop()!;
    nextLevels.set(childId, level);
    const nested = childrenByParent.get(childId);
    if (nested?.length) stack.push(...nested);
  }

  // 하위 → 상위 롤업 (max)
  let parentId = rows.find((row) => row.id === id)?.parentId ?? null;
  while (parentId) {
    const childIds = childrenByParent.get(parentId) ?? [];
    const childLevels = childIds.map(
      (childId) => nextLevels.get(childId) ?? 'NONE',
    );
    nextLevels.set(parentId, maxMenuAccessLevel(childLevels));
    parentId = rows.find((row) => row.id === parentId)?.parentId ?? null;
  }

  return rows.map((row) => ({
    ...row,
    level: nextLevels.get(row.id) ?? row.level,
  }));
};

export const mapAdminUserSummaryToRow = (
  user: AdminUserSummary,
): AdminAccountRow => ({
  id: String(user.id),
  username: user.username,
  email: user.email,
  name: user.name,
  role: normalizeAuthRole(user.roleName) || user.roleName,
  department: user.department?.trim() || '—',
  status: user.displayStatus,
  lastLoginAt:
    formatAdminDateTime(user.lastLoginAt, true, true) ??
    user.lastLoginAt ??
    null,
  createdAt: formatAdminDateTime(user.createdAt, false) ?? user.createdAt,
  phone: user.phone?.trim() || '—',
  createdBy: '—',
  lastLoginIp: null,
  mustChangePassword: user.mustChangePassword,
  deletedAt: user.deletedAt,
});

export const mapAdminUserDetailToRow = (
  user: AdminUserDetail,
): AdminAccountRow => ({
  id: String(user.id),
  username: user.username,
  email: user.email,
  name: user.name,
  role: normalizeAuthRole(user.roleName) || user.roleName,
  department: user.department?.trim() || '—',
  status: user.displayStatus,
  lastLoginAt:
    formatAdminDateTime(user.lastLoginAt, true, true) ??
    user.lastLoginAt ??
    null,
  createdAt: formatAdminDateTime(user.createdAt, false) ?? user.createdAt,
  phone: user.phone?.trim() || '—',
  createdBy: user.createdBy != null ? String(user.createdBy) : '—',
  lastLoginIp: null,
  lockedAt: formatAdminDateTime(user.lockedAt) ?? user.lockedAt,
  failedAuthenticationCount: user.failedAuthenticationCount,
  mustChangePassword: user.mustChangePassword,
  temporaryPasswordExpiresAt: user.temporaryPasswordExpiresAt,
  rowVersion: user.rowVersion,
  deletedAt: user.deletedAt,
});

/** Figma 샘플 계정 — 단위 테스트·로컬 stub용 */
export const MOCK_ADMIN_ACCOUNTS: AdminAccountRow[] = [
  {
    id: '1',
    email: 'master@rudalink.com',
    name: '김루다',
    role: 'ROLE_MASTER',
    department: '경영',
    status: 'ACTIVE',
    lastLoginAt: '2026-07-06 09:12',
    createdAt: '2026-01-05',
    phone: '010-0000-0001',
    createdBy: 'system@rudalink.com',
    lastLoginIp: '211.36.100.10',
  },
  {
    id: '2',
    email: 'admin.park@rudalink.com',
    name: '박관리',
    role: 'ROLE_MANAGER',
    department: '운영팀',
    status: 'ACTIVE',
    lastLoginAt: '2026-07-06 08:47',
    createdAt: '2026-02-10',
    phone: '010-0000-0002',
    createdBy: 'master@rudalink.com',
    lastLoginIp: '211.36.100.20',
  },
  {
    id: '3',
    email: 'cs.lee@rudalink.com',
    name: '이서연',
    role: 'ROLE_GENERAL',
    department: 'CS팀',
    status: 'ACTIVE',
    lastLoginAt: '2026-07-05 18:22',
    createdAt: '2026-03-02',
    phone: '010-1234-5678',
    createdBy: 'admin.park@rudalink.com',
    lastLoginIp: '211.36.xx.xx',
  },
  {
    id: '4',
    email: 'fin.choi@rudalink.com',
    name: '최정산',
    role: 'ROLE_GENERAL',
    department: '정산팀',
    status: 'TEMPORARY_PASSWORD',
    lastLoginAt: null,
    createdAt: '2026-07-04',
    phone: '010-0000-0004',
    createdBy: 'admin.park@rudalink.com',
    lastLoginIp: null,
  },
  {
    id: '5',
    email: 'mkt.jung@rudalink.com',
    name: '정마케',
    role: 'ROLE_GENERAL',
    department: '마케팅',
    status: 'LOCKED',
    lastLoginAt: '2026-06-30 11:05',
    createdAt: '2026-04-15',
    phone: '010-0000-0005',
    createdBy: 'admin.park@rudalink.com',
    lastLoginIp: '118.235.22.14',
    lockedAt: '2026-06-30 11:05',
    failedAuthenticationCount: 5,
  },
  {
    id: '6',
    email: 'ops.kang@rudalink.com',
    name: '강운영',
    role: 'ROLE_GENERAL',
    department: '운영팀',
    status: 'INACTIVE',
    lastLoginAt: '2026-05-21 10:33',
    createdAt: '2026-02-28',
    phone: '010-0000-0006',
    createdBy: 'admin.park@rudalink.com',
    lastLoginIp: '211.36.132.50',
  },
];

/** 단위 테스트용 축소 메뉴 권한 샘플 */
export const MOCK_MENU_PERMISSIONS: MenuPermissionRow[] = [
  { id: 'BASIC_INFO', label: '기본정보', level: 'READ' },
  { id: 'USER', label: '사용자', level: 'READ' },
  { id: 'USER_MANAGEMENT', label: '사용자관리', child: true, level: 'READ' },
  {
    id: 'CONTRACT_APPENDIX',
    label: '계약 별첨 관리',
    child: true,
    level: 'NONE',
  },
  { id: 'BASE_FEE_RATE', label: '기본 수수료율', child: true, level: 'NONE' },
  {
    id: 'DETAIL_FEE_RATE',
    label: '상세 수수료율',
    child: true,
    level: 'NONE',
  },
  { id: 'PARTNER', label: '거래처', level: 'WRITE' },
  { id: 'PRESCRIPTION', label: '처방', level: 'WRITE' },
  { id: 'SETTLEMENT', label: '정산', level: 'NONE' },
  { id: 'MAX_MATCH', label: '맥스매치', level: 'NONE' },
  { id: 'NOTICE_FAQ', label: '공지/FAQ', level: 'NONE' },
];

/** Figma「활동 로그」로그인 이력 샘플 */
export const MOCK_LOGIN_ACTIVITIES: LoginActivityRow[] = [
  {
    id: 'l1',
    at: '2026-07-05 18:22',
    ip: '211.36.132.45',
    device: 'Windows 11 · Chrome 126',
    result: 'SUCCESS',
    note: '',
  },
  {
    id: 'l2',
    at: '2026-07-05 09:03',
    ip: '211.36.132.45',
    device: 'Windows 11 · Chrome 126',
    result: 'SUCCESS',
    note: '',
  },
  {
    id: 'l3',
    at: '2026-07-04 22:47',
    ip: '118.235.14.201',
    device: 'iPhone · Safari (모바일)',
    result: 'SUCCESS',
    note: '신규 기기',
  },
  {
    id: 'l4',
    at: '2026-07-03 08:55',
    ip: '211.36.132.45',
    device: 'Windows 11 · Chrome 126',
    result: 'FAILURE',
    note: '비밀번호 오류 (1회)',
  },
  {
    id: 'l5',
    at: '2026-07-03 08:56',
    ip: '211.36.132.45',
    device: 'Windows 11 · Chrome 126',
    result: 'SUCCESS',
    note: '',
  },
];

/** Figma「활동 로그」계정 변경 이력 샘플 */
export const MOCK_ACCOUNT_CHANGES: AccountChangeRow[] = [
  {
    id: 'c1',
    at: '2026-07-05 17:02',
    summary: '권한 변경 · 회원 관리: R → RW',
    performedBy: 'admin.park@rudalink.com',
  },
  {
    id: 'c2',
    at: '2026-03-02 10:00',
    summary: '계정 생성 · 역할: 일반, 메뉴 권한 4건 부여',
    performedBy: 'admin.park@rudalink.com',
  },
];

export const findMockAdminAccount = (
  accountId: string | undefined,
): AdminAccountRow | undefined =>
  MOCK_ADMIN_ACCOUNTS.find((account) => account.id === accountId);

/**
 * 검색어·역할·상태로 목 계정을 필터링한다.
 * 검색은 이메일·이름에 부분 일치(대소문자 무시).
 */
export const filterAdminAccounts = (
  accounts: AdminAccountRow[],
  query: string,
  role: string,
  status: string,
): AdminAccountRow[] => {
  const q = query.trim().toLowerCase();
  const statusKey = status === 'TEMP_PASSWORD' ? 'TEMPORARY_PASSWORD' : status;
  return accounts.filter((account) => {
    if (role !== 'all' && account.role !== role) return false;
    if (statusKey !== 'all' && account.status !== statusKey) return false;
    if (!q) return true;
    return (
      account.email.toLowerCase().includes(q) ||
      account.name.toLowerCase().includes(q)
    );
  });
};

/**
 * 잠금 배너 문구.
 * detail의 lockedAt·실패 횟수를 우선하고, 없으면 최근 로그인 시각·IP를 사용한다.
 */
export const formatAccountLockedBanner = (account: {
  lockedAt?: string | null;
  failedAuthenticationCount?: number | null;
  lastLoginAt?: string | null;
  lastLoginIp?: string | null;
}): string => {
  const count = account.failedAuthenticationCount ?? 5;
  const at = account.lockedAt ?? account.lastLoginAt ?? '—';
  const ipPart = account.lastLoginIp ? ` · IP ${account.lastLoginIp}` : '';
  return `비밀번호 ${count}회 연속 실패로 잠금 처리된 계정입니다 (${at}${ipPart})`;
};

/** Axios 낙관적 락 충돌(409) */
export const isOptimisticLockConflict = (error: unknown): boolean => {
  const err = error as { response?: { status?: number } };
  return err?.response?.status === 409;
};

export const OPTIMISTIC_LOCK_CONFLICT_MESSAGE =
  '다른 관리자가 계정을 변경했습니다. 최신 정보를 다시 불러왔습니다.';
